import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Alert } from 'antd';
import EntityGrid, { loadData as entityGridLoadData } from 'containers/Grid/components/EntityGrid';
import EventsGridToolbar from './EventsGridToolbar';
import { formatTotal } from '../eventViewer.constants';

const StyledEntityGrid = styled(EntityGrid)`
	.ant-table td,
	.ant-table th {
		white-space: normal;
		word-wrap: break-word;
		word-break: break-word;
		overflow-wrap: break-word;
	}
`;

const formatTs = (ts) => ts ? moment(ts).format('D-MMM-YYYY HH:mm:ss') : '';

const EventsGrid = (props) => {
	const { fetchEvents, isLoading, data, grid, setGridParams, resetGridData, deviceTypesTreeData, deviceTypesIdToLabelMap } = props;

	// Only creationTime is sortable: it is the indexed column for a device; sorting by any
	// other column makes MySQL filesort every row of the device (minutes on big devices).
	const columns = [
		{
			key: 'deviceType',
			title: 'Device Type',
			dataIndex: 'deviceType',
			width: 150,
		},
		{
			key: 'deviceSerialNumber',
			title: 'Device Serial Number',
			dataIndex: 'deviceSerialNumber',
			width: 180,
		},
		{
			key: 'entryKey',
			title: 'Entry Key',
			dataIndex: 'entryKey',
			width: 150,
		},
		{
			key: 'entryValue',
			title: 'Entry Value',
			dataIndex: 'entryValue',
			width: 150,
		},
		{
			key: 'sessionId',
			title: 'Session ID',
			dataIndex: 'sessionId',
			width: 150,
		},
		{
			key: 'entryTimestamp',
			title: 'Entry Timestamp',
			dataIndex: 'entryTimestamp',
			width: 180,
			render: formatTs,
		},
		{
			key: 'localEntryTimestamp',
			title: 'Local Entry Timestamp',
			dataIndex: 'localEntryTimestamp',
			width: 180,
			render: formatTs,
		},
		{
			key: 'creationTime',
			title: 'Creation Time',
			dataIndex: 'creationTime',
			sorter: true,
			width: 180,
			render: formatTs,
		},
	];

	// Never query without a device serial number. ProcessingData has no index on
	// entryTimestamp (dropped in V9), so an unfiltered or range-only search is a full scan
	// of the table (665M rows on prod) plus a COUNT(*) per page. With a serial the query
	// is an indexed lookup. The toolbar already requires Device Type + Serial for its
	// buttons; this guards the grid's own mount / reset / pagination fetches too.
	const hasDeviceSerial = (params) => !!(params && params.filters && params.filters.deviceSerialNumber);
	const gatedFetchEvents = (params) => {
		if (hasDeviceSerial(params)) fetchEvents(params);
	};
	const serialSelected = hasDeviceSerial(grid.params);

	// Force a reload. When called with params from the toolbar, reload using the
	// toolbar's *current* state instead of the (possibly stale) Redux grid params,
	// so removing/changing a filter and hitting Refresh reflects what's on screen.
	const refreshGrid = (overrideParams) => {
		const gridToLoad = overrideParams
			? { ...grid, params: { ...grid.params, ...overrideParams } }
			: grid;
		entityGridLoadData(gridToLoad, gatedFetchEvents);
	};

	const toolbarComponent = (
		<>
			<EventsGridToolbar
				grid={grid}
				setGridParams={setGridParams}
				resetGridData={resetGridData}
				refreshGrid={refreshGrid}
				deviceTypesTreeData={deviceTypesTreeData}
				deviceTypesIdToLabelMap={deviceTypesIdToLabelMap}
			/>
			{!serialSelected && (
				<Alert
					type="info"
					showIcon
					style={{ margin: '8px 0' }}
					message="Select a Device Type and enter a Device Serial Number, then click Get Data to load events."
				/>
			)}
		</>
	);

	return (
		<StyledEntityGrid
			toolbarComponent={toolbarComponent}
			grid={grid}
			fetchData={gatedFetchEvents}
			fetchDataPending={isLoading}
			data={serialSelected ? data : []}
			columns={columns}
			rowKey={(record, index) => {
				// The backend can return rows that share an entryId (duplicate telemetry rows).
				// A non-unique React key makes the reconciler duplicate/omit rows, which left
				// stale rows from a prior query visible after filtering. Append the row index
				// so every key is unique regardless of backend data quality.
				const base = record.entryId || `${record.deviceType}-${record.entryTimestamp}-${record.entryKey}`;
				return `${base}-${index}`;
			}}
			resetGridData={resetGridData}
			setGridParams={setGridParams}
			scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
			paginationExtra={{ showTotal: (total) => formatTotal(total, 'events', grid.params.pagination) }}
		/>
	);
};

export default EventsGrid;
